import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, Save, Eye, AlertTriangle } from "lucide-react";
import api from "../../services/api";
import CostRecalcPreview from "./CostRecalcPreview";

/**
 * OrderEditDialog — single dialog reused for customer / agent / admin
 * edits. The role determines which fields render.
 *
 * Props:
 *   open / onOpenChange — controlled state
 *   order — the live order doc (so we pre-fill defaults)
 *   role  — "user" | "agent" | "admin"
 *   onSaved(newOrder) — invoked after successful PATCH so parent can
 *                        refresh its view
 */

const FIELD_GROUPS = {
  user: [
    { group: "Pickup", fields: [
      { key: "pickup_address",      label: "Pickup Address" },
      { key: "pickup_house_no",     label: "Pickup House No" },
      { key: "pickup_street",       label: "Pickup Street" },
      { key: "pickup_flat_no",      label: "Pickup Flat / Apartment No" },
      { key: "pickup_contact_name", label: "Sender Name" },
      { key: "pickup_phone",        label: "Sender Phone (10-digit)" },
    ]},
    { group: "Drop", fields: [
      { key: "delivery_address",      label: "Drop Address" },
      { key: "delivery_house_no",     label: "Drop House No" },
      { key: "delivery_street",       label: "Drop Street" },
      { key: "delivery_flat_no",      label: "Drop Flat / Apartment No" },
      { key: "delivery_contact_name", label: "Receiver Name" },
      { key: "delivery_phone",        label: "Receiver Phone (10-digit)" },
    ]},
    { group: "Package", fields: [
      { key: "package_description", label: "Package Description" },
      { key: "package_weight",      label: "Package Weight (kg)", type: "number" },
      { key: "item_size",           label: "Item Size",           options: ["small", "medium", "large"] },
      { key: "length_cm",           label: "Length (cm)",         type: "number" },
      { key: "width_cm",            label: "Width (cm)",          type: "number" },
      { key: "height_cm",           label: "Height (cm)",         type: "number" },
    ]},
    { group: "Schedule + Notes", fields: [
      { key: "scheduled_date", label: "Pickup Date" },
      { key: "scheduled_time", label: "Pickup Time" },
      { key: "notes",          label: "Delivery Instructions", multiline: true },
    ]},
  ],
  agent: [
    { group: "Operational", fields: [
      { key: "package_weight", label: "Actual Weight (kg)", type: "number" },
      { key: "length_cm",      label: "Length (cm)",        type: "number" },
      { key: "width_cm",       label: "Width (cm)",         type: "number" },
      { key: "height_cm",      label: "Height (cm)",        type: "number" },
      { key: "notes",          label: "Delivery Notes",     multiline: true },
      { key: "agent_notes",    label: "Agent Notes (customer availability, etc.)", multiline: true },
    ]},
  ],
  admin: [
    // Admin sees customer + agent groups; we surface both inline below.
  ],
};

// Minimal field config for admin — superset of customer + agent + admin-only.
const ADMIN_EXTRA = [
  { group: "Admin Only", fields: [
    { key: "status",            label: "Status" },
    { key: "payment_status",    label: "Payment Status" },
    { key: "admin_notes",       label: "Admin Notes",         multiline: true },
    { key: "cancellation_reason", label: "Cancellation Reason", multiline: true },
  ]},
];

export default function OrderEditDialog({ open, onOpenChange, order, role, onSaved }) {
  const r = (role || "user").toLowerCase();
  const groups =
    r === "admin"
      ? [...FIELD_GROUPS.user, ...FIELD_GROUPS.agent, ...ADMIN_EXTRA]
      : (FIELD_GROUPS[r] || FIELD_GROUPS.user);

  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [needsRequest, setNeedsRequest] = useState(false);

  useEffect(() => {
    // Reset state on open
    if (open) {
      setDraft({});
      setReason("");
      setPreview(null);
      setErr(null);
      setNeedsRequest(false);
    }
  }, [open, order?.tracking_id]);

  if (!order) return null;

  const reasonRequired = r === "admin" || r === "agent";
  // Drop empty / unchanged fields before sending.
  const buildDiffPayload = () => {
    const out = {};
    for (const [k, v] of Object.entries(draft)) {
      if (v === "" || v === undefined) continue;
      const orig = order[k];
      const cmp = typeof orig === "number" ? Number(v) : v;
      if (cmp !== orig) out[k] = cmp;
    }
    return out;
  };

  const handlePreview = async () => {
    const fields = buildDiffPayload();
    if (!Object.keys(fields).length) {
      setErr("Nothing changed yet.");
      return;
    }
    setPreviewing(true);
    setErr(null);
    try {
      const { data } = await api.patch(`/orders/${order.tracking_id}`, {
        fields, dry_run: true, reason: reason || "preview",
      });
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    const fields = buildDiffPayload();
    if (!Object.keys(fields).length) {
      setErr("Nothing changed yet.");
      return;
    }
    if (reasonRequired && reason.trim().length < 4) {
      setErr("Reason is required (min 4 characters).");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.patch(`/orders/${order.tracking_id}`, {
        fields, reason: reason.trim() || undefined,
      });
      onSaved && onSaved(data?.order, data?.diff);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed";
      // Customer trying to edit post-pickup → guide them to the
      // request flow which the parent can launch.
      if (typeof detail === "string" && detail.includes("edit-request")) {
        setNeedsRequest(true);
      }
      setErr(detail);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitRequest = async () => {
    const fields = buildDiffPayload();
    if (!Object.keys(fields).length) {
      setErr("Nothing changed yet.");
      return;
    }
    if (reason.trim().length < 4) {
      setErr("Please tell admin WHY you need this change (min 4 chars).");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/orders/${order.tracking_id}/edit-request`, {
        fields, reason: reason.trim(),
      });
      onOpenChange(false);
      // Surface a success toast through the parent — onSaved called
      // with null order signals "submitted as request".
      onSaved && onSaved(null, [{ field: "edit_request", new_value: "submitted" }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-bold">
            {needsRequest ? "Request Edit Approval" : "Edit Order"} · {order.tracking_id}
          </DialogTitle>
          <DialogDescription>
            {needsRequest
              ? "Pickup has started, so direct edits are locked. Submit a request — admin will review and apply if approved."
              : reasonRequired
                ? "Edits as " + r + " are tracked in the audit log. Reason is required."
                : "Editable fields are limited until pickup starts. Save will recompute price if needed."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {groups.map((g) => (
            <div key={g.group} className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{g.group}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {g.fields.map((f) => {
                  const val = draft[f.key] !== undefined ? draft[f.key] : (order[f.key] ?? "");
                  const tid = `edit-${f.key}`;
                  if (f.options) {
                    return (
                      <div key={f.key}>
                        <Label className="text-xs text-slate-600">{f.label}</Label>
                        <select
                          value={val}
                          onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                          className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:border-[#FF6B00] focus:outline-none"
                          data-testid={tid}
                        >
                          {f.options.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  if (f.multiline) {
                    return (
                      <div key={f.key} className="sm:col-span-2">
                        <Label className="text-xs text-slate-600">{f.label}</Label>
                        <Textarea
                          value={val}
                          onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                          rows={2}
                          className="rounded-lg border-slate-200 text-sm"
                          data-testid={tid}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={f.key}>
                      <Label className="text-xs text-slate-600">{f.label}</Label>
                      <Input
                        type={f.type || "text"}
                        value={val}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                        className="rounded-lg border-slate-200 h-10 text-sm"
                        data-testid={tid}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <Label className="text-xs text-slate-600">
              {reasonRequired ? "Reason (required)" : needsRequest ? "Why do you need this change?" : "Reason (optional)"}
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={
                r === "agent"
                  ? "e.g. actual weight different, customer unavailable…"
                  : r === "admin"
                    ? "e.g. correcting customer typo, admin override…"
                    : "Optional — helps us understand"
              }
              className="rounded-lg border-slate-200 text-sm"
              data-testid="edit-reason"
            />
          </div>

          {/* Price preview block */}
          {preview && (
            <div data-testid="edit-preview-block">
              <CostRecalcPreview preview={preview} />
              {preview.diff?.length ? (
                <ul className="mt-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-[11px] text-slate-700 space-y-0.5">
                  {preview.diff.map((d, i) => (
                    <li key={`${d.field || "diff"}-${i}`} className="font-mono">
                      <span className="font-bold text-slate-500">{d.field}:</span>{" "}
                      <span className="line-through opacity-60">{String(d.old_value ?? "—")}</span>{" "}
                      → <b>{String(d.new_value ?? "—")}</b>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}

          {err && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2" data-testid="edit-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>{err}</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="edit-cancel-btn">
            Cancel
          </Button>
          {!needsRequest && r !== "admin" && (
            <Button variant="outline" onClick={handlePreview} disabled={previewing} className="rounded-xl" data-testid="edit-preview-btn">
              {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Preview
            </Button>
          )}
          {needsRequest ? (
            <Button onClick={handleSubmitRequest} disabled={busy} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl" data-testid="edit-submit-request-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Submit Request
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={busy} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl" data-testid="edit-save-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Changes
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
